import { Directive, ElementRef, inject, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { DragDropService } from './drag-drop.service';

/**
 * Auto-scrolls the host scroll container while an item is dragged near its top or
 * bottom edge. A rAF loop keeps scrolling even when the cursor is held still at the
 * edge, where `dragover` stops firing.
 */
@Directive({
  selector: '[coinDragAutoScroll]',
  standalone: false
})
export class DragAutoScrollDirective {
  private readonly elem = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly dragDropService = inject(DragDropService);

  private readonly edgeThreshold = 60;
  private readonly maxSpeed = 18;

  private speed = 0;
  private frame: number | null = null;

  constructor() {
    const el = this.elem.nativeElement;
    this.zone.runOutsideAngular(() => {
      fromEvent<DragEvent>(el, 'dragover')
        .pipe(takeUntilDestroyed())
        .subscribe(event => this.onDragOver(event));
      fromEvent<DragEvent>(el, 'dragleave')
        .pipe(takeUntilDestroyed())
        .subscribe(event => this.onDragLeave(event));
      fromEvent(el, 'drop')
        .pipe(takeUntilDestroyed())
        .subscribe(() => this.stop());
      fromEvent(window, 'dragend')
        .pipe(takeUntilDestroyed())
        .subscribe(() => this.stop());
    });
  }

  private onDragOver(event: DragEvent): void {
    if (!this.dragDropService.isDragging()) {
      return;
    }
    const rect = this.elem.nativeElement.getBoundingClientRect();
    const topDistance = event.clientY - rect.top;
    const bottomDistance = rect.bottom - event.clientY;

    if (topDistance < this.edgeThreshold) {
      this.speed = -this.speedFor(topDistance);
    } else if (bottomDistance < this.edgeThreshold) {
      this.speed = this.speedFor(bottomDistance);
    } else {
      this.stop();
      return;
    }
    this.start();
  }

  private onDragLeave(event: DragEvent): void {
    // Ignore leave events fired while crossing into a child; only stop when the
    // cursor is actually outside the container bounds.
    const rect = this.elem.nativeElement.getBoundingClientRect();
    const outside =
      event.clientX < rect.left ||
      event.clientX >= rect.right ||
      event.clientY < rect.top ||
      event.clientY >= rect.bottom;
    if (outside) {
      this.stop();
    }
  }

  private speedFor(distance: number): number {
    const ratio = Math.max(0, this.edgeThreshold - distance) / this.edgeThreshold;
    return Math.ceil(ratio * this.maxSpeed);
  }

  private start(): void {
    if (this.frame !== null) {
      return;
    }
    const step = (): void => {
      this.elem.nativeElement.scrollTop += this.speed;
      this.frame = requestAnimationFrame(step);
    };
    this.frame = requestAnimationFrame(step);
  }

  private stop(): void {
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
    this.speed = 0;
  }
}
